import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only clear tokens for actual authentication failures
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const errorDetail = error.response.data?.detail || '';
      const errorMessage = error.response.data?.message || '';
      
      // Only clear tokens for explicit authentication failures
      if (requestUrl.includes('/auth/login') || 
          requestUrl.includes('/auth/register') ||
          errorDetail === 'Invalid token' ||
          errorDetail === 'Token expired' ||
          errorMessage === 'Invalid authentication credentials' ||
          errorMessage === 'Authentication required') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        console.log('Authentication tokens cleared due to auth failure:', errorDetail || errorMessage);
        
        // Optionally redirect to home or trigger a re-authentication
        // window.location.href = '/';
      } else {
        // For other 401s (like profile not found, insufficient permissions), preserve tokens
        console.log('401 error but preserving tokens:', requestUrl, errorDetail || errorMessage);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },
  
  getProfile: async () => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await apiClient.put('/auth/profile', profileData);
    return response.data;
  }
};

// Contact API
export const contactAPI = {
  sendMessage: async (messageData) => {
    const response = await apiClient.post('/contact/message', messageData);
    return response.data;
  },
  
  subscribeNewsletter: async (email) => {
    const response = await apiClient.post('/contact/newsletter/subscribe', { email });
    return response.data;
  }
};

// Genealogy API (for future implementation)
export const genealogyAPI = {
  startAnalysis: async (analysisData) => {
    const response = await apiClient.post('/genealogy/analyze', analysisData);
    return response.data;
  },
  
  startEnhancedAnalysis: async (enhancedData) => {
    const response = await apiClient.post('/genealogy/analyze-enhanced', enhancedData);
    return response.data;
  },
  
  createAnalysis: async (analysisData) => {
    // Handle tribal genealogy analysis
    if (analysisData.analysis_type === 'tribal_genealogy') {
      const response = await apiClient.post('/genealogy/tribal-analysis', analysisData);
      return response.data;
    }
    // Handle other analysis types
    const response = await apiClient.post('/genealogy/analyze', analysisData);
    return response.data;
  },
  
  getAnalyses: async () => {
    const response = await apiClient.get('/genealogy/analyses');
    return response.data;
  },
  
  getAnalysis: async (analysisId) => {
    const response = await apiClient.get(`/genealogy/analysis/${analysisId}`);
    return response.data;
  }
};

// Testimonials API (for future implementation)
export const testimonialsAPI = {
  getPublicTestimonials: async (limit = 10, featuredOnly = false) => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (featuredOnly) params.append('featured_only', 'true');
    
    const response = await apiClient.get(`/testimonials/public?${params}`);
    return response.data;
  },
  
  createTestimonial: async (testimonialData) => {
    const response = await apiClient.post('/testimonials/create', testimonialData);
    return response.data;
  }
};

// Crest API
export const crestAPI = {
  generateCrest: async (generateRequest) => {
    const response = await apiClient.post('/crest/generate-dalle', generateRequest);
    return response.data;
  },
  
  getUserCrests: async () => {
    const response = await apiClient.get('/crest/my-crests');
    return response.data;
  },
  
  getCrest: async (crestId) => {
    const response = await apiClient.get(`/crest/${crestId}`);
    return response.data;
  },
  
  getInspirationCrests: async () => {
    const response = await apiClient.get('/crest/library/inspiration');
    return response.data;
  },
  
  createCustomCrest: async (crestData) => {
    const response = await apiClient.post('/crest/create-custom', crestData);
    return response.data;
  }
};

// Healing API
export const healingAPI = {
  seekWisdom: async (sessionData) => {
    const response = await apiClient.post('/healing/seek-wisdom', sessionData);
    return response.data;
  },
  
  getUserSessions: async () => {
    const response = await apiClient.get('/healing/my-sessions');
    return response.data;
  },
  
  getSession: async (sessionId) => {
    const response = await apiClient.get(`/healing/session/${sessionId}`);
    return response.data;
  },
  
  getTraditionalRemedies: async (tradition = null, category = null) => {
    const params = new URLSearchParams();
    if (tradition) params.append('tradition', tradition);
    if (category) params.append('category', category);
    
    const response = await apiClient.get(`/healing/remedies/traditional?${params}`);
    return response.data;
  },
  
  generateHealingCard: async (sessionId) => {
    const response = await apiClient.get(`/healing/session/${sessionId}/healing-card`);
    return response.data;
  }
};

// Language API
export const languageAPI = {
  getLanguages: async (family = null, region = null) => {
    const params = new URLSearchParams();
    if (family) params.append('family', family);
    if (region) params.append('region', region);
    
    const response = await apiClient.get(`/language/languages?${params}`);
    return response.data;
  },
  
  getPhrases: async (languageCode, category = null, level = null) => {
    const params = new URLSearchParams();
    params.append('language_code', languageCode);
    if (category) params.append('category', category);
    if (level) params.append('level', level);
    
    const response = await apiClient.get(`/language/phrases?${params}`);
    return response.data;
  },
  
  startLearning: async (progressData) => {
    const response = await apiClient.post('/language/start-learning', progressData);
    return response.data;
  },
  
  getUserProgress: async () => {
    const response = await apiClient.get('/language/my-progress');
    return response.data;
  },
  
  startPractice: async (sessionData) => {
    const response = await apiClient.post('/language/practice', sessionData);
    return response.data;
  },
  
  generateAudio: async (phraseId, audioRequest) => {
    const response = await apiClient.post(`/language/phrases/${phraseId}/generate-audio`, audioRequest);
    return response.data;
  },
  
  updateProgress: async (languageCode, phraseId, learned) => {
    const params = new URLSearchParams();
    params.append('phrase_id', phraseId);
    params.append('learned', learned.toString());
    
    const response = await apiClient.put(`/language/progress/${languageCode}/update?${params}`);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await apiClient.get('/language/categories');
    return response.data;
  }
};

// Vocabulary API (New tribal vocabulary system)
export const vocabularyAPI = {
  getLanguages: async (region = null, family = null, status = null) => {
    const params = new URLSearchParams();
    if (region) params.append('region', region);
    if (family) params.append('family', family);
    if (status) params.append('lang_status', status);
    
    const response = await apiClient.get(`/vocabulary/languages?${params}`);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await apiClient.get('/vocabulary/categories');
    return response.data;
  },
  
  getPhrases: async (languageCode, category = null, difficulty = null, sacred = null, limit = 50) => {
    const params = new URLSearchParams();
    params.append('language_code', languageCode);
    if (category) params.append('category', category);
    if (difficulty) params.append('difficulty', difficulty);
    if (sacred !== null) params.append('sacred', sacred.toString());
    params.append('limit', limit.toString());
    
    const response = await apiClient.get(`/vocabulary/phrases?${params}`);
    return response.data;
  },
  
  getPhraseDetails: async (phraseId) => {
    const response = await apiClient.get(`/vocabulary/phrase/${phraseId}`);
    return response.data;
  },
  
  searchVocabulary: async (query, languageCode = null, category = null, limit = 20) => {
    const params = new URLSearchParams();
    params.append('query', query);
    if (languageCode) params.append('language_code', languageCode);
    if (category) params.append('category', category);
    params.append('limit', limit.toString());
    
    const response = await apiClient.get(`/vocabulary/search?${params}`);
    return response.data;
  },
  
  getStats: async () => {
    const response = await apiClient.get('/vocabulary/stats');
    return response.data;
  }
};

// Tribal Table API
export const tribalTableAPI = {
  createMealPlan: async (planRequest) => {
    const response = await apiClient.post('/tribal-table/meal-plan', planRequest);
    return response.data;
  },
  
  searchRecipes: async (searchParams) => {
    const params = new URLSearchParams();
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key]) params.append(key, searchParams[key]);
    });
    
    const response = await apiClient.get(`/tribal-table/recipes/search?${params}`);
    return response.data;
  },
  
  getUserMealPlans: async () => {
    const response = await apiClient.get('/tribal-table/my-meal-plans');
    return response.data;
  },
  
  getMealPlan: async (planId) => {
    const response = await apiClient.get(`/tribal-table/meal-plan/${planId}`);
    return response.data;
  },
  
  getSeasonalGuide: async (season = null, region = null) => {
    const params = new URLSearchParams();
    if (season) params.append('season', season);
    if (region) params.append('region', region);
    
    const response = await apiClient.get(`/tribal-table/seasonal-guide?${params}`);
    return response.data;
  },
  
  getFoodAsMedicine: async (foodName = null, medicinalUse = null, season = null) => {
    const params = new URLSearchParams();
    if (foodName) params.append('food_name', foodName);
    if (medicinalUse) params.append('medicinal_use', medicinalUse);
    if (season) params.append('season', season);
    
    const response = await apiClient.get(`/tribal-table/food-as-medicine?${params}`);
    return response.data;
  }
};

// Treaty API
export const treatyAPI = {
  interpretTreaty: async (interpretRequest) => {
    // Try public endpoint first for demo, fallback to authenticated endpoint
    try {
      const response = await apiClient.post('/treaty/interpret-public', interpretRequest);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        // Fallback to authenticated endpoint
        const response = await apiClient.post('/treaty/interpret', interpretRequest);
        return response.data;
      }
      throw error;
    }
  },
  
  searchTreaties: async (searchParams) => {
    const params = new URLSearchParams();
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key]) {
        if (key === 'year_start' || key === 'year_end') {
          params.append(key, searchParams[key].toString());
        } else {
          params.append(key, searchParams[key]);
        }
      }
    });
    
    const response = await apiClient.get(`/treaty/search?${params}`);
    return response.data;
  },
  
  getUserInterpretations: async () => {
    const response = await apiClient.get('/treaty/my-interpretations');
    return response.data;
  },
  
  getInterpretation: async (interpretationId) => {
    const response = await apiClient.get(`/treaty/interpretation/${interpretationId}`);
    return response.data;
  },
  
  analyzeLandRights: async (analysisRequest) => {
    const response = await apiClient.post('/treaty/land-rights-analysis', analysisRequest);
    return response.data;
  },
  
  getMasterclasses: async (difficulty = null, topic = null) => {
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    if (topic) params.append('topic', topic);
    
    const response = await apiClient.get(`/treaty/masterclasses?${params}`);
    return response.data;
  }
};

// Subscription API
export const subscriptionAPI = {
  getPlans: async () => {
    const response = await apiClient.get('/subscription/plans');
    return response.data;
  },
  
  getCurrentSubscription: async () => {
    const response = await apiClient.get('/subscription/my-subscription');
    return response.data;
  },
  
  createCheckoutSession: async (subscriptionData) => {
    const response = await apiClient.post('/subscription/checkout', subscriptionData);
    return response.data;
  },
  
  checkPaymentStatus: async (sessionId) => {
    const response = await apiClient.get(`/subscription/payment-status/${sessionId}`);
    return response.data;
  },
  
  upgradeToFree: async () => {
    const response = await apiClient.post('/subscription/upgrade-to-free');
    return response.data;
  },
  
  getUsageStats: async () => {
    const response = await apiClient.get('/subscription/usage');
    return response.data;
  }
};

// Notification API
export const notificationAPI = {
  getNotifications: async (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        searchParams.append(key, params[key]);
      }
    });
    
    const response = await apiClient.get(`/notifications/?${searchParams}`);
    return response.data;
  },

  getCounts: async () => {
    const response = await apiClient.get('/notifications/counts/');
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await apiClient.put(`/notifications/${notificationId}/`, {
      status: 'read'
    });
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await apiClient.put('/notifications/mark-all-read/');
    return response.data;
  },

  deleteNotification: async (notificationId) => {
    const response = await apiClient.delete(`/notifications/${notificationId}/`);
    return response.data;
  },

  getPreferences: async () => {
    const response = await apiClient.get('/notifications/preferences/');
    return response.data;
  },

  updatePreferences: async (preferences) => {
    const response = await apiClient.put('/notifications/preferences/', preferences);
    return response.data;
  },

  createTestNotification: async () => {
    const response = await apiClient.post('/notifications/test/');
    return response.data;
  },

  cleanupExpired: async () => {
    const response = await apiClient.post('/notifications/cleanup/');
    return response.data;
  }
};

// Ceremony API  
export const ceremonyAPI = {
  searchCeremonies: async (searchParams) => {
    const params = new URLSearchParams();
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key]) {
        params.append(key, searchParams[key]);
      }
    });
    
    const response = await apiClient.get(`/ceremony/search?${params}`);
    return response.data;
  },
  
  getCeremonyDetails: async (ceremonyId) => {
    const response = await apiClient.get(`/ceremony/${ceremonyId}`);
    return response.data;
  },
  
  getCeremoniesByTribe: async (tribeName) => {
    const response = await apiClient.get(`/ceremony/tribe/${tribeName}`);
    return response.data;
  },
  
  getTribalGuides: async () => {
    const response = await apiClient.get('/ceremony/guides/all');
    return response.data;
  }
};

// Rights Report API
export const rightsReportAPI = {
  generateReport: async (reportRequest = {}) => {
    const response = await apiClient.post('/rights-report/generate', {
      include_detailed_treaties: true,
      include_cultural_heritage: true,
      include_legal_guidance: true,
      format: 'pdf',
      ...reportRequest
    });
    return response.data;
  },
  
  downloadReport: async (reportId) => {
    const response = await apiClient.get(`/rights-report/download/${reportId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Plant Remedy API
export const plantRemedyAPI = {
  searchPlantRemedies: async (searchParams = {}) => {
    const params = new URLSearchParams();
    
    if (searchParams.query) params.append('query', searchParams.query);
    if (searchParams.tribe) params.append('tribe', searchParams.tribe);
    if (searchParams.region) params.append('region', searchParams.region);
    if (searchParams.plant_type) params.append('plant_type', searchParams.plant_type);
    if (searchParams.medicinal_use) params.append('medicinal_use', searchParams.medicinal_use);
    if (searchParams.sacred_only) params.append('sacred_only', searchParams.sacred_only);
    if (searchParams.limit) params.append('limit', searchParams.limit);

    const response = await apiClient.get(`/plant-remedies/search?${params.toString()}`);
    return response.data;
  },
  
  getPlantRemedy: async (plantId) => {
    const response = await apiClient.get(`/plant-remedies/${plantId}`);
    return response.data;
  },
  
  getTribes: async () => {
    const response = await apiClient.get('/plant-remedies/tribes/list');
    return response.data;
  },
  
  getRegions: async () => {
    const response = await apiClient.get('/plant-remedies/regions/list');
    return response.data;
  },
  
  getPlantTypes: async () => {
    const response = await apiClient.get('/plant-remedies/plant-types/list');
    return response.data;
  },
  
  getMedicinalUses: async () => {
    const response = await apiClient.get('/plant-remedies/medicinal-uses/list');
    return response.data;
  }
};

// Tribal Wall API
export const tribalWallAPI = {
  // Feed
  getFeed: async (params = {}) => {
    const searchParams = new URLSearchParams();
    
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.cursor) searchParams.append('cursor', params.cursor);
    if (params.post_type) searchParams.append('post_type', params.post_type);
    if (params.tribe_tag) searchParams.append('tribe_tag', params.tribe_tag);
    if (params.hashtag) searchParams.append('hashtag', params.hashtag);
    if (params.featured_only) searchParams.append('featured_only', params.featured_only);
    
    const response = await apiClient.get(`/tribal-wall/feed?${searchParams.toString()}`);
    return response.data;
  },
  
  // Posts
  createPost: async (postData) => {
    const response = await apiClient.post('/tribal-wall/posts', postData);
    return response.data;
  },
  
  getPost: async (postId) => {
    const response = await apiClient.get(`/tribal-wall/posts/${postId}`);
    return response.data;
  },
  
  uploadPostMedia: async (postId, formData) => {
    const response = await apiClient.post(`/tribal-wall/posts/${postId}/media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Engagement
  likePost: async (postId) => {
    const response = await apiClient.post(`/tribal-wall/posts/${postId}/like`);
    return response.data;
  },
  
  repostPost: async (postId, comment = null) => {
    const formData = new FormData();
    if (comment) formData.append('comment', comment);
    
    const response = await apiClient.post(`/tribal-wall/posts/${postId}/repost`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Comments
  createComment: async (postId, content, parentCommentId = null) => {
    const formData = new FormData();
    formData.append('content', content);
    if (parentCommentId) formData.append('parent_comment_id', parentCommentId);
    
    const response = await apiClient.post(`/tribal-wall/posts/${postId}/comments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  getPostComments: async (postId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset) searchParams.append('offset', params.offset);
    
    const response = await apiClient.get(`/tribal-wall/posts/${postId}/comments?${searchParams.toString()}`);
    return response.data;
  },
  
  // Trending & Discovery
  getTrendingHashtags: async (limit = 10) => {
    const response = await apiClient.get(`/tribal-wall/trending-hashtags?limit=${limit}`);
    return response.data;
  },
  
  // User Badges
  getUserBadges: async (userId) => {
    const response = await apiClient.get(`/tribal-wall/user/${userId}/badges`);
    return response.data;
  },
  
  // Admin Functions
  getFeaturedPosts: async () => {
    const response = await apiClient.get('/tribal-wall/admin/featured-posts');
    return response.data;
  },
  
  toggleFeaturedPost: async (postId) => {
    const response = await apiClient.post(`/tribal-wall/admin/posts/${postId}/feature`);
    return response.data;
  }
};

// User Follow API
export const userFollowAPI = {
  followUser: async (userId) => {
    const response = await apiClient.post('/user-follow/follow', {
      following_id: userId
    });
    return response.data;
  },
  
  unfollowUser: async (userId) => {
    const response = await apiClient.delete(`/user-follow/unfollow/${userId}`);
    return response.data;
  },
  
  getFollowers: async () => {
    const response = await apiClient.get('/user-follow/followers');
    return response.data;
  },
  
  getFollowing: async () => {
    const response = await apiClient.get('/user-follow/following');
    return response.data;
  },
  
  getCircleMembers: async (circleId) => {
    const response = await apiClient.get(`/user-follow/circle/${circleId}/members`);
    return response.data;
  },
  
  getUserProfile: async (userId) => {
    const response = await apiClient.get(`/user-follow/profile/${userId}`);
    return response.data;
  }
};

// Public Profile API
export const publicProfileAPI = {
  getProfile: async (userId) => {
    const response = await apiClient.get(`/public-profile/profile/${userId}`);
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await apiClient.put('/public-profile/profile', profileData);
    return response.data;
  },
  
  discoverProfiles: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.skip) searchParams.append('skip', params.skip);
    if (params.tribal_affiliation) searchParams.append('tribal_affiliation', params.tribal_affiliation);
    if (params.region) searchParams.append('region', params.region);
    
    const response = await apiClient.get(`/public-profile/profiles/discover?${searchParams.toString()}`);
    return response.data;
  }
};

// Marketplace API
export const marketplaceAPI = {
  getProducts: async (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        searchParams.append(key, params[key]);
      }
    });
    
    const response = await apiClient.get(`/marketplace/products?${searchParams.toString()}`);
    return response.data;
  },
  
  createProduct: async (productData) => {
    const response = await apiClient.post('/marketplace/products', productData);
    return response.data;
  },
  
  getProduct: async (productId) => {
    const response = await apiClient.get(`/marketplace/products/${productId}`);
    return response.data;
  },
  
  createOrder: async (orderData) => {
    const response = await apiClient.post('/marketplace/orders', orderData);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await apiClient.get('/marketplace/categories');
    return response.data;
  },
  
  getMyProducts: async () => {
    const response = await apiClient.get('/marketplace/my-products');
    return response.data;
  }
};

// Tribal Social API
export const tribalSocialAPI = {
  // Follow System
  followUser: async (userId, followType = 'general') => {
    const response = await apiClient.post('/tribal-social/follow', {
      user_id: userId,
      follow_type: followType
    });
    return response.data;
  },
  
  unfollowUser: async (userId) => {
    const response = await apiClient.delete(`/tribal-social/follow/${userId}`);
    return response.data;
  },
  
  getFollowers: async (userId = null, limit = 20, offset = 0) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('limit', limit);
    params.append('offset', offset);
    
    const response = await apiClient.get(`/tribal-social/followers?${params}`);
    return response.data;
  },
  
  getFollowing: async (userId = null, limit = 20, offset = 0) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('limit', limit);
    params.append('offset', offset);
    
    const response = await apiClient.get(`/tribal-social/following?${params}`);
    return response.data;
  },
  
  getFollowSuggestions: async (limit = 10) => {
    const response = await apiClient.get(`/tribal-social/suggestions?limit=${limit}`);
    return response.data;
  },
  
  getFollowStatus: async (userId) => {
    const response = await apiClient.get(`/tribal-social/follow-status/${userId}`);
    return response.data;
  },
  
  // Private Messaging
  sendMessage: async (receiverId, content, messageType = 'text', metadata = {}) => {
    const response = await apiClient.post('/tribal-social/messages/send', {
      receiver_id: receiverId,
      content,
      message_type: messageType,
      metadata
    });
    return response.data;
  },
  
  sendPeacePipe: async (receiverId, content, metadata = {}) => {
    const response = await apiClient.post('/tribal-social/messages/peace-pipe', {
      receiver_id: receiverId,
      content,
      metadata
    });
    return response.data;
  },
  
  sendKinshipRequest: async (receiverId, content, metadata = {}) => {
    const response = await apiClient.post('/tribal-social/messages/kinship-request', {
      receiver_id: receiverId,
      content,
      metadata
    });
    return response.data;
  },
  
  getConversations: async (limit = 20) => {
    const response = await apiClient.get(`/tribal-social/conversations?limit=${limit}`);
    return response.data;
  },
  
  getConversationMessages: async (conversationId, limit = 50, offset = 0) => {
    const response = await apiClient.get(`/tribal-social/conversations/${conversationId}?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  
  getMessageTemplates: async () => {
    const response = await apiClient.get('/tribal-social/messages/templates');
    return response.data;
  },
  
  // Delete Message
  deleteMessage: async (messageId) => {
    const response = await apiClient.delete(`/tribal-social/messages/${messageId}`);
    return response.data;
  },
  
  // Delete Conversation
  deleteConversation: async (conversationId) => {
    const response = await apiClient.delete(`/tribal-social/conversations/${conversationId}`);
    return response.data;
  },
  
  // Social Feed
  getSocialFeed: async (limit = 20, offset = 0) => {
    const response = await apiClient.get(`/tribal-social/social-feed?limit=${limit}&offset=${offset}`);
    return response.data;
  }
};

// Social Feed API
export const socialFeedAPI = {
  // Create post
  createPost: async (postData) => {
    const response = await apiClient.post('/social-feed/posts', postData);
    return response.data;
  },

  // Get single post
  getPost: async (postId) => {
    const response = await apiClient.get(`/social-feed/posts/${postId}`);
    return response.data;
  },

  // Delete post
  deletePost: async (postId) => {
    const response = await apiClient.delete(`/social-feed/posts/${postId}`);
    return response.data;
  },

  // Get following feed
  getFollowingFeed: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.skip) searchParams.append('skip', params.skip);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.tribal_region) searchParams.append('tribal_region', params.tribal_region);
    if (params.hashtag) searchParams.append('hashtag', params.hashtag);
    
    const response = await apiClient.get(`/social-feed/feed/following?${searchParams.toString()}`);
    return response.data;
  },

  // Get discover feed
  getDiscoverFeed: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.skip) searchParams.append('skip', params.skip);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.tribal_region) searchParams.append('tribal_region', params.tribal_region);
    if (params.hashtag) searchParams.append('hashtag', params.hashtag);
    
    const response = await apiClient.get(`/social-feed/feed/discover?${searchParams.toString()}`);
    return response.data;
  },

  // Add reaction
  addReaction: async (postId, reactionType) => {
    const response = await apiClient.post(`/social-feed/posts/${postId}/react?reaction_type=${reactionType}`);
    return response.data;
  },

  // Remove reaction
  removeReaction: async (postId) => {
    const response = await apiClient.delete(`/social-feed/posts/${postId}/react`);
    return response.data;
  },

  // Add comment
  addComment: async (postId, commentData) => {
    const response = await apiClient.post(`/social-feed/posts/${postId}/comment`, {
      post_id: postId,
      ...commentData
    });
    return response.data;
  },

  // Get comments
  getComments: async (postId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.skip) searchParams.append('skip', params.skip);
    if (params.limit) searchParams.append('limit', params.limit);
    
    const response = await apiClient.get(`/social-feed/posts/${postId}/comments?${searchParams.toString()}`);
    return response.data;
  },

  // Repost
  repost: async (postId, comment = null) => {
    const response = await apiClient.post(`/social-feed/posts/${postId}/repost`, {
      original_post_id: postId,
      comment
    });
    return response.data;
  },

  // Remove repost
  removeRepost: async (postId) => {
    const response = await apiClient.delete(`/social-feed/posts/${postId}/repost`);
    return response.data;
  },

  // Get smoke signal status
  getSmokeSignalStatus: async () => {
    const response = await apiClient.get('/social-feed/smoke-signal/status');
    return response.data;
  }
};

// Utility functions
export const setAuthToken = (token) => {
  localStorage.setItem('auth_token', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
};

export const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

export default apiClient;